import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Sparkles } from 'lucide-react';
import { participantService, ParticipantAvatar, SessionState } from '../../../services/participant.service';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  avatarName: string;
  timestamp: Date;
}

interface Props {
  sessionId: string;
  state: SessionState;
}

export function ChatTab({ sessionId, state }: Props) {
  const [selectedAvatarId, setSelectedAvatarId] = useState<string>('guide');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const guideName = state.guide?.name || 'Ce-Ce';
  const allAvatars: Array<{ id: string; name: string; imageUrl?: string; primaryColor?: string }> = [
    { id: 'guide', name: guideName },
    ...state.avatars,
  ];
  const selectedAvatar = allAvatars.find(a => a.id === selectedAvatarId) ?? allAvatars[0];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      text,
      avatarName: 'You',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setSending(true);
    try {
      const result = await participantService.sendChat(
        sessionId,
        text,
        selectedAvatarId === 'guide' ? undefined : selectedAvatarId
      );
      setMessages(prev => [...prev, {
        id: result.messageId,
        role: 'assistant',
        text: result.response,
        avatarName: selectedAvatar?.name ?? guideName,
        timestamp: new Date(),
      }]);
    } catch (err: any) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        text: 'Something went wrong — please try again.',
        avatarName: selectedAvatar?.name ?? guideName,
        timestamp: new Date(),
      }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Avatar selector */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-none border-b border-white/10 flex-shrink-0">
        {allAvatars.map(avatar => (
          <button
            key={avatar.id}
            onClick={() => setSelectedAvatarId(avatar.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium flex-shrink-0 transition-all ${
              selectedAvatarId === avatar.id
                ? 'bg-primary text-navy'
                : 'bg-white/10 text-white/70'
            }`}
          >
            {avatar.id === 'guide' ? (
              <Sparkles size={12} />
            ) : (avatar as ParticipantAvatar).imageUrl ? (
              <img src={(avatar as ParticipantAvatar).imageUrl} alt="" className="w-4 h-4 rounded-full object-cover" />
            ) : (
              <div
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: (avatar as ParticipantAvatar).primaryColor || '#334155' }}
              />
            )}
            {avatar.name}
          </button>
        ))}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            {selectedAvatarId === 'guide' ? (
              <>
                <div className="w-12 h-12 bg-primary/20 rounded-2xl flex items-center justify-center mb-3">
                  <Sparkles size={20} className="text-primary" />
                </div>
                <p className="text-white font-semibold mb-1">Chat with {guideName}</p>
                <p className="text-white/40 text-sm max-w-56">
                  Ask about the session, what's been discussed, or what's coming next.
                </p>
              </>
            ) : (
              <p className="text-white/40 text-sm">Start a conversation with {selectedAvatar?.name}</p>
            )}
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-primary text-navy font-medium'
                : 'bg-white/10 text-white'
            }`}>
              {msg.role === 'assistant' && (
                <p className="text-xs text-white/40 mb-1 font-medium">{msg.avatarName}</p>
              )}
              {msg.role === 'assistant' ? (
                <div className="prose prose-invert prose-sm max-w-none
                  prose-p:my-1 prose-li:my-0
                  prose-table:border-collapse
                  prose-td:border prose-td:border-white/20 prose-td:px-2 prose-td:py-1
                  prose-th:border prose-th:border-white/20 prose-th:px-2 prose-th:py-1 prose-th:bg-white/10">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                </div>
              ) : msg.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white/10 rounded-2xl px-4 py-3">
              <div className="flex gap-1 items-center">
                <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce [animation-delay:0ms]" />
                <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce [animation-delay:150ms]" />
                <span className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="flex-shrink-0 px-4 pb-4 pt-2 border-t border-white/10">
        <div className="flex gap-2 items-end">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={`Message ${selectedAvatar?.name ?? guideName}…`}
            className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-primary transition-all resize-none"
            maxLength={1000}
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className="w-11 h-11 bg-primary rounded-xl flex items-center justify-center flex-shrink-0 disabled:opacity-40 active:scale-90 transition-all"
          >
            <Send size={16} className="text-navy" />
          </button>
        </div>
      </form>
    </div>
  );
}
